import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "../../components/shared/Card/Card";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import useTickets from "../../data/useTickets";
import { useUserLookup } from "../../hooks/useUserLookup";
import { TicketPriority, TicketStatus } from "../../models/Ticket";

const statusText: Record<number, string> = {
  [TicketStatus.Open]: "Open",
  [TicketStatus.InProgress]: "In Progress",
  [TicketStatus.Resolved]: "Resolved",
  [TicketStatus.Closed]: "Closed",
};

const priorityText: Record<number, string> = {
  [TicketPriority.Low]: "Low",
  [TicketPriority.Medium]: "Medium",
  [TicketPriority.High]: "High",
  [TicketPriority.Urgent]: "Urgent",
};

const statusStyles: Record<number, string> = {
  [TicketStatus.Open]: "bg-green-50 text-green-700 ring-green-600/20",
  [TicketStatus.InProgress]: "bg-amber-50 text-amber-700 ring-amber-600/20",
  [TicketStatus.Resolved]: "bg-blue-50 text-blue-700 ring-blue-600/20",
  [TicketStatus.Closed]: "bg-slate-100 text-slate-700 ring-slate-500/20",
};

const priorityStyles: Record<number, string> = {
  [TicketPriority.Low]: "bg-slate-100 text-slate-700 ring-slate-500/20",
  [TicketPriority.Medium]: "bg-amber-50 text-amber-700 ring-amber-600/20",
  [TicketPriority.High]: "bg-orange-50 text-orange-700 ring-orange-600/20",
  [TicketPriority.Urgent]: "bg-red-50 text-red-700 ring-red-600/20",
};

const pageSize = 8;

type SortOption = "newest" | "oldest" | "priorityHigh" | "priorityLow";

const sortOptions: { value: SortOption; label: string }[] = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "priorityHigh", label: "Priority (High-Low)" },
  { value: "priorityLow", label: "Priority (Low-High)" },
];

export default function Tickets() {
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { userId } = useAuth();
  const { tickets } = useTickets();
  const { findUser } = useUserLookup();
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | TicketStatus>("all");
  const [priorityFilter, setPriorityFilter] = useState<"all" | TicketPriority>("all");
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setInfo({
      name: "Support Tickets",
      desc: "Manage your support requests and responses",
    });
  }, [setInfo]);

  const rows = tickets.data?.data ?? [];

  const getCreatedBy = (createdByUserId: string) => {
    const user = findUser(createdByUserId);
    if (!user) return "Unknown";

    const fullName = `${user.forename ?? ""} ${user.surname ?? ""}`.trim();
    return fullName || user.email || user.username || "Unknown";
  };

  const getInitials = (name: string) => {
    const cleaned = name.trim();
    if (!cleaned) return "UN";

    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  };

  const getLatestNote = (ticket: (typeof rows)[number]) => {
    if (!ticket.notes?.length) return null;

    return [...ticket.notes]
      .sort((a, b) => +new Date(b.created) - +new Date(a.created))[0] ?? null;
  };


  const stats = useMemo(() => {
    const openCount = rows.filter((ticket) =>
      [TicketStatus.Open, TicketStatus.InProgress].includes(ticket.status)
    ).length;
    const closedCount = rows.filter((ticket) => ticket.status === TicketStatus.Closed).length;
    const urgentCount = rows.filter((ticket) => ticket.priority === TicketPriority.Urgent).length;

    return {
      openCount,
      closedCount,
      urgentCount

    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();

    const filtered = rows.filter((ticket) => {
      const createdBy = getCreatedBy(ticket.createdByUserId).toLowerCase();
      const matchesSearch =
        needle.length === 0 ||
        ticket.subject.toLowerCase().includes(needle) ||
        ticket.description.toLowerCase().includes(needle) ||
        createdBy.includes(needle);
      const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
      const matchesPriority = priorityFilter === "all" || ticket.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesPriority;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === "newest") return +new Date(b.created) - +new Date(a.created);
      if (sortBy === "oldest") return +new Date(a.created) - +new Date(b.created);
      if (sortBy === "priorityHigh") return b.priority - a.priority;
      return a.priority - b.priority;
    });
  }, [rows, searchText, statusFilter, priorityFilter, sortBy]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, statusFilter, priorityFilter, sortBy]);

  const pageCount = Math.max(Math.ceil(filteredRows.length / pageSize), 1);
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage]);

  const statusFilterOptions: { value: "all" | TicketStatus; label: string }[] = [
    { value: "all", label: "All Statuses" },
    { value: TicketStatus.Open, label: statusText[TicketStatus.Open] },
    { value: TicketStatus.InProgress, label: statusText[TicketStatus.InProgress] },
    { value: TicketStatus.Resolved, label: statusText[TicketStatus.Resolved] },
    { value: TicketStatus.Closed, label: statusText[TicketStatus.Closed] },
  ];

  const priorityFilterOptions: { value: "all" | TicketPriority; label: string }[] = [
    { value: "all", label: "All Priorities" },
    { value: TicketPriority.Low, label: priorityText[TicketPriority.Low] },
    { value: TicketPriority.Medium, label: priorityText[TicketPriority.Medium] },
    { value: TicketPriority.High, label: priorityText[TicketPriority.High] },
    { value: TicketPriority.Urgent, label: priorityText[TicketPriority.Urgent] },
  ];

  return (
    <div className="mt-4 space-y-4">
      <Card>
        <div className="border-b border-slate-200 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Support Tickets</h2>
              <p className="mt-1 text-sm text-slate-600">
                Manage your support requests and responses
              </p>
            </div>

            <button
              type="button"
              className="inline-flex items-center rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-purple-700"
              onClick={() => navigate("/tickets/new")}
            >
              <span className="mr-2">➕</span>
              Create Ticket
            </button>
          </div>
        </div>
      </Card>

      <Card>
        <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">📩 Open</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.openCount}</p>
            <p className="text-sm text-slate-500">tickets</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">✅ Closed</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.closedCount}</p>
            <p className="text-sm text-slate-500">tickets</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">🔴 Urgent</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.urgentCount}</p>
            <p className="text-sm text-slate-500">tickets</p>
          </div>

        </div>
      </Card>

      <Card>
        <div className="space-y-4 p-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label className="xl:col-span-2">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Search
              </span>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search tickets..."
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
            </label>

            <label>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Status
              </span>
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value === "all" ? "all" : Number(e.target.value))
                }
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              >
                {statusFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Priority
              </span>
              <select
                value={priorityFilter}
                onChange={(e) =>
                  setPriorityFilter(e.target.value === "all" ? "all" : Number(e.target.value))
                }
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              >
                {priorityFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Sort
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </Card>

      <Card>
        <div className="space-y-3 p-4">
          {tickets.isLoading ? (
            <div className="min-h-[35vh] flex items-center justify-center">
              <LoadingWheel />
            </div>
          ) : tickets.isError || !tickets.data ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">
                An error occurred retrieving tickets.
              </p>
              <p className="mt-1 text-xs text-red-600">
                You can still create a new support ticket using the button above.
              </p>
            </div>
          ) : pagedRows.length === 0 ? (
            <p className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
              No tickets match the current search and filters.
            </p>
          ) : (
            <div className="space-y-3">
              {pagedRows.map((ticket) => {
                const createdBy = getCreatedBy(ticket.createdByUserId);
                const replyCount = ticket.notes?.length ?? 0;
                const latestNote = getLatestNote(ticket);
                const hasNewMessage =
                  !!userId &&
                  ticket.createdByUserId.toLowerCase() === userId.toLowerCase() &&
                  ticket.status === TicketStatus.Open &&
                  !!latestNote &&
                  latestNote.isFromStaff === true;
                const lastReply = ticket.notes?.length
                  ? new Date(
                    [...ticket.notes]
                      .map((note) => new Date(note.created))
                      .sort((a, b) => b.getTime() - a.getTime())[0]
                  ).toLocaleString()
                  : "No replies yet";

                return (
                  <button
                    key={ticket.id}
                    type="button"
                    className="w-full rounded-2xl border border-slate-200 bg-[#f8f8f8] p-4 text-left transition hover:cursor-pointer hover:border-purple-400 hover:shadow-lg"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="line-clamp-1 text-base font-semibold text-slate-900">{ticket.subject}</p>
                        <p className="mt-1 line-clamp-2 text-sm text-slate-600">{ticket.description}</p>
                      </div>
                      {hasNewMessage ? (
                        <span className="inline-flex shrink-0 items-center rounded-full bg-purple-50 px-2 py-1 text-xs font-semibold text-purple-700 ring-1 ring-purple-600/20">
                          ● New reply
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusStyles[ticket.status]
                          }`}
                      >
                        {statusText[ticket.status]}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${priorityStyles[ticket.priority]
                          }`}
                      >
                        {priorityText[ticket.priority]}
                      </span>
                      <span className="text-xs text-slate-500">💬 {replyCount} replies</span>
                      <span className="text-xs text-slate-500">🕒 Last reply {lastReply}</span>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-xs font-semibold text-purple-700">
                          {getInitials(createdBy)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{createdBy}</p>
                          <p className="text-xs text-slate-500">
                            📅 {new Date(ticket.created).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <span className="text-sm font-semibold text-purple-700">View Ticket →</span>
                    </div>
                  </button>
                );
              })}

              <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                <p className="text-xs text-slate-500">
                  Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, filteredRows.length)} of{" "}
                  {filteredRows.length} tickets
                </p>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                    disabled={currentPage === 1}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-slate-600">
                    Page {currentPage} of {pageCount}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(page + 1, pageCount))}
                    disabled={currentPage >= pageCount}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
